import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import {
  deleteToolShortcut,
  getToolShortcuts,
  upsertToolShortcut,
  type ToolShortcut,
} from '../../api/toolShortcuts.api';

// Mirrors `MAX_SLOTS` in apps/api/src/agents/shortcuts/agent-shortcut.service.ts.
export const TOOL_SHORTCUT_SLOTS = 4;

const buildQueryKey = (workspaceId: string | null) => ['tool-shortcuts', workspaceId] as const;

export const useToolShortcuts = () => {
  const workspaceId = useCurrentWorkspaceId();

  const query = useQuery({
    queryKey: buildQueryKey(workspaceId),
    queryFn: getToolShortcuts,
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000,
  });

  const slots = useMemo<(ToolShortcut | null)[]>(() => {
    const filled: (ToolShortcut | null)[] = Array.from({ length: TOOL_SHORTCUT_SLOTS }, () => null);
    for (const item of query.data ?? []) {
      if (item.slotIndex >= 0 && item.slotIndex < TOOL_SHORTCUT_SLOTS) {
        filled[item.slotIndex] = item;
      }
    }
    return filled;
  }, [query.data]);

  return {
    slots,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export const useUpsertToolShortcut = () => {
  const { t } = useTranslation('springboard');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation({
    mutationFn: ({ slotIndex, agentId }: { slotIndex: number; agentId: string }) =>
      upsertToolShortcut(slotIndex, agentId),
    // Write the confirmed shortcut into the cache immediately: `slots` (and
    // the agentId a `ShortcutSlot` renders from) is derived from this query,
    // so waiting only on `invalidateQueries`'s refetch left a window where
    // the slot's old (empty) value was still current — the tile flashed back
    // to empty between leaving edit mode and the refetch landing.
    onSuccess: (saved) => {
      queryClient.setQueryData<ToolShortcut[]>(buildQueryKey(workspaceId), (previous) => [
        ...(previous ?? []).filter((shortcut) => shortcut.slotIndex !== saved.slotIndex),
        saved,
      ]);
      void queryClient.invalidateQueries({ queryKey: buildQueryKey(workspaceId) });
    },
    // A rejected request does not prove the write never landed — it can fail
    // in transit after the server applied it — and `staleTime` would otherwise
    // keep serving the pre-mutation layout for five minutes, so the failure
    // path refetches instead of trusting the cache.
    onError: () => {
      notificationService.showError(t('dashboard.useToolShortcuts.couldNotSaveTheShortcut'), {
        description: t('dashboard.useToolShortcuts.yourShortcutsWereRefreshed'),
      });
      void queryClient.invalidateQueries({ queryKey: buildQueryKey(workspaceId) });
    },
  });
};

export const useDeleteToolShortcut = () => {
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation({
    mutationFn: (slotIndex: number) => deleteToolShortcut(slotIndex),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: buildQueryKey(workspaceId) });
    },
  });
};

/**
 * Removes slot N and shifts all subsequent filled slots one position to the left.
 * e.g. removing slot 1 from [A, B, C, D] → [A, C, D, _]
 */
export const useShiftDeleteToolShortcut = () => {
  const { t } = useTranslation('springboard');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation({
    mutationFn: async ({
      slotIndex,
      slots,
    }: {
      slotIndex: number;
      slots: readonly (ToolShortcut | null)[];
    }) => {
      // Collect all slots after the removed one that are filled
      const toShift = slots.slice(slotIndex + 1).filter((s): s is ToolShortcut => s !== null);

      // Delete the target slot
      await deleteToolShortcut(slotIndex);

      // Upsert each subsequent filled slot at its new (index - 1) position
      for (const slot of toShift) {
        await upsertToolShortcut(slot.slotIndex - 1, slot.agentId);
      }

      // If the last slot was vacated by a shift, delete it
      const lastShifted = toShift.at(-1);
      if (lastShifted) {
        await deleteToolShortcut(lastShifted.slotIndex);
      }
    },
    // Same reasoning as the upsert above: write the post-shift result into the
    // cache immediately rather than waiting on `invalidateQueries`'s refetch,
    // which left the removed slot's old (filled) value current for a moment —
    // the tile flashed back to filled right after its exit animation.
    onSuccess: (_data, { slotIndex, slots }) => {
      queryClient.setQueryData<ToolShortcut[]>(buildQueryKey(workspaceId), () => {
        const next = [...slots];
        for (let i = slotIndex; i < next.length - 1; i++) {
          const shifted = next[i + 1];
          next[i] = shifted ? { slotIndex: i, agentId: shifted.agentId } : null;
        }
        next[next.length - 1] = null;
        return next.filter((shortcut): shortcut is ToolShortcut => shortcut !== null);
      });
      void queryClient.invalidateQueries({ queryKey: buildQueryKey(workspaceId) });
    },
    // The shift is several requests with no transaction around them, so a
    // failure can leave the server holding a layout that is neither the old
    // one nor the intended one. Nothing local can reconstruct that, so the
    // cache is dropped and refetched rather than patched.
    onError: () => {
      notificationService.showError(t('dashboard.useToolShortcuts.couldNotRemoveTheShortcut'), {
        description: t('dashboard.useToolShortcuts.yourShortcutsWereRefreshed'),
      });
      void queryClient.invalidateQueries({ queryKey: buildQueryKey(workspaceId) });
    },
  });
};
